import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';
import { cameras } from '../cameraData';
import { canonId } from '../exifIdentify';
import CameraIdentifyModal from './CameraIdentifyModal';

const t = (key, data) => app.translator.trans('acseven-chdk-cameras.forum.identify.' + key, data);

/**
 * The strip above the camera index: identify a camera, and its Canon firmware
 * version, from a photo it took.
 *
 * Owners know the name on the front of the camera; almost nobody knows the
 * firmware version, and it is the one thing needed to pick a build. Canon
 * writes both into every JPEG (MakerNote tags 0x10 and 0x1e), so the photo
 * answers it -- read here in the browser, never uploaded.
 *
 * The outcome opens in a modal: a hit links to the camera page with the
 * version marked, a shared model id offers both, and an unreadable file says
 * why. The row itself never changes height.
 */
export default class CameraIdentifyBar extends Component {
  oninit(vnode) {
    super.oninit(vnode);

    this.busy = false;
    this.over = false;
  }

  view() {
    return (
      <div
        className={'CameraIdentifyBar' + (this.over ? ' CameraIdentifyBar--over' : '')}
        // The whole row takes the drop -- aiming a dragged file at a button
        // is the fiddly part. dragover fires many times a second, so it only
        // redraws on the edges (Mithril skips the redraw on `return false`).
        ondragover={(e) => {
          e.preventDefault();
          if (this.over) return false;
          this.over = true;
        }}
        ondragleave={() => (this.over = false)}
        ondrop={(e) => {
          e.preventDefault();
          this.over = false;
          if (e.dataTransfer.files[0]) this.read(e.dataTransfer.files[0]);
        }}
      >
        <div className="CameraIdentifyBar-action Button Button--primary">
          <input
            className="CameraIdentifyBar-file"
            type="file"
            accept="image/jpeg,.jpg,.jpeg"
            oncreate={(vnode) => (this.file = vnode.dom)}
            onchange={(e) => e.target.files[0] && this.read(e.target.files[0])}
          />
          <Button
            className="Button CameraIdentifyBar-choose"
            icon="fas fa-folder-open"
            loading={this.busy}
            disabled={this.busy}
            onclick={() => this.file && this.file.click()}
          >
            {t('choose')}
          </Button>
        </div>

        <div className="CameraIdentifyBar-text">
          <h3 className="CameraIdentifyBar-title">{t('title')}</h3>
          <p className="CameraIdentifyBar-body">
            {t('body')} <span className="CameraIdentifyBar-privacy">{t('privacy')}</span>
          </p>
        </div>
      </div>
    );
  }

  read(file) {
    this.busy = true;

    // The Exif segment sits at the head of the file and is at most 64 KB, so a
    // slice is enough -- no reason to pull a 10 MB photo into memory.
    //
    // Nothing here outlives the read: the slice is the only copy, it is never
    // put on `this` and never sent anywhere, so it is collectable as soon as
    // the parse returns. Clearing the input below drops the browser's own
    // handle on the file too.
    file
      .slice(0, 262144)
      .arrayBuffer()
      .then((buf) => {
        const id = canonId(new Uint8Array(buf));
        // Two model pairs share one model id (IXUS 132 / 135, N / N Facebook),
        // so the modal offers both and the owner picks.
        const hits = cameras.filter((c) => c.mid === id.mid);

        this.busy = false;

        // A Canon that is not in the index has no port: same answer as
        // "Camera not listed?", so the modal points there.
        if (!hits.length) throw { kind: 'unknown' };

        app.modal.show(CameraIdentifyModal, { hits, fw: id.fw });
        m.redraw();
      })
      .catch((e) => {
        this.busy = false;
        app.modal.show(CameraIdentifyModal, { hits: [], error: e.kind || 'no_exif' });
        m.redraw();
      });

    // Drops the file from the input -- the browser holds no handle on it
    // afterwards, and the same photo picked twice in a row fires again (a
    // change event needs the value to differ).
    if (this.file) this.file.value = '';
  }
}
